import type { ColumnFilters, ColumnFilterValue } from '@/models/flowFilter.type';

export type ColumnFilterKind = ColumnFilterValue['kind'];

/** Type de filtre attendu par colonne, pour relire les filtres depuis l'URL. */
export type ColumnFilterKinds = Record<string, ColumnFilterKind>;

const RANGE_SEPARATOR = '..';
const LIST_SEPARATOR = ',';
const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

const splitRange = (raw: string): [string, string] | undefined => {
  const index = raw.indexOf(RANGE_SEPARATOR);
  if (index < 0) return undefined;
  return [raw.slice(0, index), raw.slice(index + RANGE_SEPARATOR.length)];
};

const parseNumber = (raw: string): number | undefined => {
  if (raw.trim() === '') return undefined;
  const value = Number(raw);
  return Number.isFinite(value) ? value : undefined;
};

const serializeValue = (value: ColumnFilterValue): string => {
  switch (value.kind) {
    case 'text':
      return value.value;
    case 'select':
      return value.values.join(LIST_SEPARATOR);
    case 'number-range':
      return `${value.min ?? ''}${RANGE_SEPARATOR}${value.max ?? ''}`;
    case 'date-range':
      return `${value.from ?? ''}${RANGE_SEPARATOR}${value.to ?? ''}`;
    case 'boolean':
      return String(value.value);
  }
};

const parseValue = (kind: ColumnFilterKind, raw: string): ColumnFilterValue | undefined => {
  switch (kind) {
    case 'text':
      return raw.trim() ? { kind, value: raw } : undefined;
    case 'select': {
      const values = raw.split(LIST_SEPARATOR).filter(Boolean);
      return values.length > 0 ? { kind, values } : undefined;
    }
    case 'number-range': {
      const range = splitRange(raw);
      if (!range) return undefined;
      const min = parseNumber(range[0]);
      const max = parseNumber(range[1]);
      return min == null && max == null ? undefined : { kind, min, max };
    }
    case 'date-range': {
      const range = splitRange(raw);
      if (!range) return undefined;
      const from = DATE_PATTERN.test(range[0]) ? range[0] : undefined;
      const to = DATE_PATTERN.test(range[1]) ? range[1] : undefined;
      return from == null && to == null ? undefined : { kind, from, to };
    }
    case 'boolean':
      return raw === 'true' || raw === 'false' ? { kind, value: raw === 'true' } : undefined;
  }
};

/** Relit les filtres des colonnes connues ; les valeurs invalides et les autres paramètres sont ignorés. */
export const searchParamsToFilters = (
  params: URLSearchParams,
  kinds: ColumnFilterKinds,
): ColumnFilters =>
  Object.entries(kinds).reduce<ColumnFilters>((filters, [accessor, kind]) => {
    const raw = params.get(accessor);
    const value = raw == null ? undefined : parseValue(kind, raw);
    return value ? { ...filters, [accessor]: value } : filters;
  }, {});

/** Écrit les filtres dans les paramètres (retire d'abord ceux des colonnes connues). */
export const applyFiltersToSearchParams = (
  params: URLSearchParams,
  filters: ColumnFilters,
  kinds: ColumnFilterKinds,
): URLSearchParams => {
  const next = new URLSearchParams(params);
  Object.keys(kinds).forEach(accessor => next.delete(accessor));
  Object.entries(filters).forEach(([accessor, value]) => {
    if (accessor in kinds) next.set(accessor, serializeValue(value));
  });
  return next;
};
